import type { QuizConfig, QuizOption } from '../types';
import { ANALYTICS_EVENTS } from '@/features/analytics';
import quizStep01 from '../assets/quiz-step-01.webp';
import quizStep02 from '../assets/quiz-step-02.webp';
import quizStep03 from '../assets/quiz-step-03.webp';
import quizStep04 from '../assets/quiz-step-04.webp';
import quizStep05 from '../assets/quiz-step-05.webp';
import quizStep06 from '../assets/quiz-step-06.webp';
import quizStep07 from '../assets/quiz-step-07.webp';
import quizStep08 from '../assets/quiz-step-08.webp';
import quizStep09 from '../assets/quiz-step-09.webp';
import quizStep10 from '../assets/quiz-step-10.webp';

const YES_NO_OPTIONS: QuizOption[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'Prefer not to say', value: 'skip' },
];

export const settlementQuiz: QuizConfig = {
  id: 'settlement',
  name: 'Settlement Quiz',
  steps: [
    {
      id: 'gender',
      eventOnShow: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_LOADED,
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_1GENDER,
      type: 'single-choice',
      title: 'What’s your gender?',
      subtitle: 'Some settlements use this to check eligibility. Totally optional.',
      image: quizStep01,
      // Figma frame 344:14494 — 258×128 @1x (asset is 2×)
      imageClassName: 'h-[128px] w-auto max-w-[258px]',
      required: true,
      options: [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
        { label: 'Prefer not to say', value: 'skip' },
      ],
    },
    {
      id: 'facebook_instagram',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_2FACEBOOK,
      type: 'single-choice',
      title: 'Ever posted, liked, or scrolled through Facebook or Instagram?',
      image: quizStep02,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'visa_mastercard',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_3VISA,
      type: 'single-choice',
      title: 'Swiped a Visa or Mastercard at checkout in the U.S.?',
      image: quizStep03,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'yahoo_mail',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_4YAHOO,
      type: 'single-choice',
      title: 'Had a Yahoo Mail account any time between 2012 and 2016?',
      image: quizStep04,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'tmobile',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_5TMOBILE,
      type: 'single-choice',
      title: 'Been a T-Mobile customer, even years ago?',
      image: quizStep05,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'credit_bureaus',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_6CREDIT,
      type: 'single-choice',
      title:
        "Ever applied for a loan, credit card, or apartment? You've probably got a file with Equifax, Experian, or TransUnion.",
      image: quizStep06,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: '23andme',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_723ANDME,
      type: 'single-choice',
      title: 'Spit in a tube for 23andMe or a DNA test like it?',
      image: quizStep07,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'amazon',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_8AMAZON,
      type: 'single-choice',
      title: 'Ever ordered anything — literally anything — on Amazon?',
      image: quizStep08,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'marriott',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_9MARRIOTT,
      type: 'single-choice',
      title: 'Checked into a Marriott anywhere between 2014 and 2020?',
      image: quizStep09,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'google_gmail',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_10GOOGLE,
      type: 'single-choice',
      title: 'Ever searched something on Google or opened a Gmail inbox?',
      image: quizStep10,
      required: true,
      options: YES_NO_OPTIONS,
    },
    {
      id: 'processing',
      eventOnShow: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_PROCESSING,
      type: 'processing',
      title: 'Estimating Your Payouts',
      fullBleed: true,
      countsTowardProgress: false,
    },
    {
      id: 'email',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_EMAIL,
      type: 'email',
      title: 'Enter your email address to start tracking unclaimed money instantly',
      subtitle: "We'll notify you the moment a new payout or settlement matches your profile",
      placeholder: 'Enter your email',
      ctaLabel: 'Get started',
      required: true,
      fullBleed: true,
      countsTowardProgress: false,
    },
    {
      id: 'claims',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_CLAIMS,
      type: 'claims',
      title: 'Your Available Claims',
      fullBleed: true,
      countsTowardProgress: false,
    },
    {
      id: 'profile',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_PROFILE,
      type: 'profile',
      title: 'Fill out your details',
      fullBleed: true,
      countsTowardProgress: false,
    },
    {
      id: 'signing',
      event: ANALYTICS_EVENTS.SETTLEMENT_QUIZ_SIGNING,
      type: 'signing',
      title: 'Sign your claims',
      fullBleed: true,
      countsTowardProgress: false,
    },
  ],
};
